"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import {
  configureGrowthAnalytics,
  GROWTH_EVENT_NAMES,
  resolveGrowthSiteContext,
  trackGrowthEvent,
  type GrowthSiteContext,
} from "@/lib/analytics/growthAnalytics";
import {
  captureGrowthAttribution,
  consumePendingGoogleSignUp,
  growthEventAttribution,
  recordContentAttribution,
  recordToolAttribution,
} from "@/lib/analytics/growthAttribution";

const TOOL_PATH_PATTERN = /^\/araclar\/([^/]+)\/?$/;
const TOOL_CALCULATE_LABEL_PATTERN =
  /(^|\s)(hesapla|hesaplayin|hesaplamayi)(\s|$)/i;
const BLOG_PATH_PATTERN = /^\/(?:en\/)?blog\/([^/]+)\/?$/;
const MONEY_PAGE_PATH_PATTERN =
  /^\/(?:en\/)?(?:ai-advisory|capabilities|engineering|governance|pilot|proof|solutions|workspace-agents)\/?$/;
const CTA_DESTINATION_PATTERN =
  /^\/(?:en\/)?(?:register|contact|iletisim|pilot|demo|book-demo|chat-bridge\/contact)\/?$/;
const TRUSTED_WHATSAPP_HOSTS = new Set(["wa.me", "api.whatsapp.com"]);

function classifyCtaTarget(destination: URL | null): string | null {
  if (!destination) return null;
  if (destination.protocol === "mailto:") return "email";
  if (
    destination.protocol === "https:" &&
    TRUSTED_WHATSAPP_HOSTS.has(destination.hostname.toLocaleLowerCase("en-US"))
  ) {
    return "whatsapp";
  }
  if (
    destination.origin === window.location.origin &&
    CTA_DESTINATION_PATTERN.test(destination.pathname)
  ) {
    return destination.pathname;
  }
  return null;
}

function isToolCalculateTarget(target: Element): boolean {
  if (target.closest('[data-growth-event="tool_calculate"]')) return true;
  const button = target.closest<HTMLButtonElement>("button");
  if (!button || button.disabled) return false;
  const label = (button.textContent || button.getAttribute("aria-label") || "")
    .trim()
    .toLocaleLowerCase("tr-TR");
  return TOOL_CALCULATE_LABEL_PATTERN.test(label);
}

function trackPendingGoogleSignUp(): void {
  const pendingSignUp = consumePendingGoogleSignUp(
    window.location.pathname,
    window.location.search
  );
  if (!pendingSignUp) return;
  trackGrowthEvent(GROWTH_EVENT_NAMES.SIGN_UP, {
    conversion_event_id: pendingSignUp.conversionEventId,
    form_name: "register",
    method: "google",
    ...growthEventAttribution(window.location.pathname),
  });
}

function handleToolInteraction(target: Element): boolean {
  const toolMatch = window.location.pathname.match(TOOL_PATH_PATTERN);
  if (!toolMatch) return false;

  const toolSlug = toolMatch[1];
  if (isToolCalculateTarget(target)) {
    recordToolAttribution(window.location.pathname, toolSlug);
    trackGrowthEvent(GROWTH_EVENT_NAMES.TOOL_CALCULATE, {
      ...growthEventAttribution(window.location.pathname),
      source_path: window.location.pathname,
      tool_slug: toolSlug,
    });
  }

  const toolLink = target.closest<HTMLAnchorElement>("a[href]");
  if (!toolLink) return true;
  const toolDestination = new URL(toolLink.href, window.location.origin);
  if (
    toolDestination.origin !== window.location.origin ||
    toolDestination.pathname !== "/register"
  ) {
    return true;
  }
  recordToolAttribution(window.location.pathname, toolSlug);
  trackGrowthEvent(GROWTH_EVENT_NAMES.TOOL_CTA_CLICK, {
    ...growthEventAttribution(window.location.pathname),
    source_path: window.location.pathname,
    tool_slug: toolSlug,
    cta_target: toolDestination.pathname,
  });
  return true;
}

function handleGrowthClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (handleToolInteraction(target)) return;

  const blogMatch = window.location.pathname.match(BLOG_PATH_PATTERN);
  const moneyPageMatch = window.location.pathname.match(
    MONEY_PAGE_PATH_PATTERN
  );
  if (!blogMatch && !moneyPageMatch) return;

  const ctaElement = target.closest<HTMLElement>("[data-growth-cta]");
  const link = target.closest<HTMLAnchorElement>("a[href]");
  const destination = link ? new URL(link.href, window.location.origin) : null;
  const classifiedTarget = classifyCtaTarget(destination);
  if ((!ctaElement && !classifiedTarget) || (destination && !classifiedTarget))
    return;

  const contentType = blogMatch ? "blog" : "money_page";
  const contentSlug =
    blogMatch?.[1] ||
    moneyPageMatch?.[0].split("/").filter(Boolean).join(":") ||
    "root";
  const ctaTarget = classifiedTarget || "onsite_action";
  const ctaId =
    ctaElement?.dataset.growthCta ||
    ctaTarget.split("/").filter(Boolean).join(":") ||
    "primary";
  recordContentAttribution(window.location.pathname, contentType, contentSlug);
  trackGrowthEvent(GROWTH_EVENT_NAMES.CONTENT_CTA_CLICK, {
    ...growthEventAttribution(window.location.pathname),
    source_path: window.location.pathname,
    content_type: contentType,
    content_slug: contentSlug,
    cta_id: ctaId,
    cta_target: ctaTarget,
    interaction_status: "intent_only",
  });
}

export default function GrowthAnalyticsBridge({
  analyticsEnabled,
  siteContext: explicitSiteContext,
}: {
  analyticsEnabled: boolean;
  siteContext?: GrowthSiteContext;
}) {
  const pathname = usePathname();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    const siteContext =
      explicitSiteContext || resolveGrowthSiteContext(window.location.hostname);
    configureGrowthAnalytics(analyticsEnabled, siteContext);
    if (!analyticsEnabled || !siteContext) {
      lastTrackedPath.current = null;
      return;
    }
    const currentPath = pathname || window.location.pathname;
    if (lastTrackedPath.current !== currentPath) {
      captureGrowthAttribution(currentPath, window.location.search);
      const blogMatch = currentPath.match(BLOG_PATH_PATTERN);
      if (siteContext.brand === "gaiai" && blogMatch) {
        recordContentAttribution(currentPath, "blog", blogMatch[1]);
      }
      trackPendingGoogleSignUp();
      lastTrackedPath.current = currentPath;
    }
    document.addEventListener("click", handleGrowthClick, true);
    return () => document.removeEventListener("click", handleGrowthClick, true);
  }, [analyticsEnabled, explicitSiteContext, pathname]);

  return null;
}
